/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { UserAtom } from "../../../../../atoms/UserAtom";
import { IAlias } from "../../../../../generated/Api";
import StyledButton from "../../../../StyledButton";
import StyledInput from "../../../../StyledInput";
import { UserAlias } from "./AddressesSettings";

const Body = styled.div`
  width: fit-content;
  height: fit-content;

  position: relative;
  border-top: 1px solid #eceeee;
  box-sizing: inherit;

  margin: 20px -22px 0px;

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: calc(100% - 68px);
    margin: 0;
    padding: 0 0 96px;

    justify-content: start;
    grid-gap: 32px;

    border-top: none;

    position: relative;
  }
`;

const Content = styled.div`
  width: 100%;
  height: 100%;

  padding: 48px 24px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  grid-gap: 24px;

  @media screen and (max-width: 1024px) {
    justify-content: start;
  }
`;

const Bottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  border-top: 1px solid #eceeee;
  padding: 24px 20px 0px;
  background-color: #fff;

  @media screen and (max-width: 1024px) {
    background: white;
    border-top: 1px solid #cccccc;

    padding: 25px;

    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;

    justify-content: flex-end;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: row;
`;

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 600;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 300px;

  @media screen and (max-width: 1024px) {
    width: 100%;
  }
`;

interface Props {
  addUserAlias: (alias: UserAlias) => void;

  userAliasBeingEdited?: {
    index: number;
    alias: IAlias;
  };

  editUserAlias: (alias: IAlias) => void;
  deleteUserAlias: (alias: IAlias) => void;
  onCancel: () => void;
  closeModal: () => void;

  blockDeletion?: boolean;
}

export default function AddAddresses(props: Props): JSX.Element {
  const {
    addUserAlias,
    editUserAlias,
    deleteUserAlias,
    onCancel,
    userAliasBeingEdited,
    closeModal,
  } = props;

  const user = useRecoilValue(UserAtom);
  const [fromName, setFromName] = useState<string>("");
  const [address, setEmailAddresses] = useState<string>("");

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (userAliasBeingEdited) {
      setFromName(userAliasBeingEdited.alias.fromName);
      setEmailAddresses(userAliasBeingEdited.alias.address);
    }
  }, [userAliasBeingEdited]);

  return (
    <Body>
      <Content>
        <InputWrapper>
          <Title>Name</Title>
          <StyledInput
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            placeholder="Enter the name"
            css={css`
              margin-top: 5px;
            `}
          />
        </InputWrapper>
        <InputWrapper>
          <Title>Email Address</Title>
          <StyledInput
            value={address}
            onChange={(e) => setEmailAddresses(e.target.value)}
            placeholder="Enter the email address"
            css={css`
              margin-top: 5px;
            `}
          />
        </InputWrapper>
      </Content>
      <Bottom>
        <Buttons>
          {userAliasBeingEdited && !props.blockDeletion && (
            <StyledButton
              onClick={() => {
                deleteUserAlias(userAliasBeingEdited.alias);
                closeModal();
              }}
              inverted
              css={css`
                width: 80px;
                margin-right: 10px;
                color: red;

                border: none;
              `}
            >
              Delete
            </StyledButton>
          )}
          <StyledButton
            onClick={onCancel}
            inverted
            css={css`
              width: 80px;
              margin-right: 10px;
            `}
          >
            Cancel
          </StyledButton>
          <StyledButton
            css={css`
              width: 80px;
            `}
            isLoading={isLoading}
            onClick={() => {
              setIsLoading(true);

              const aliasAddressDomain = address.split("@")[1]?.trim();

              const domainFoundInNewDomains =
                user!.domains &&
                user!.domains.find((d) => d.url === aliasAddressDomain);

              console.log({
                user,
                aliasAddressDomain,
                domainFoundInNewDomains,
              });

              if (
                // New method, multiple domains per account
                !domainFoundInNewDomains &&
                // Old method, single domain per account
                aliasAddressDomain !== user!.domain!.url
              ) {
                setIsLoading(false);
                return toast(
                  () => (
                    <div>
                      The email <br />
                      <b>{address}</b>
                      <br />
                      <br />
                      has a different domain than the default domain for the
                      account
                      <br />
                      <b>@{user?.domain?.url}</b>
                    </div>
                  ),
                  { type: "error" }
                );
              }

              if (userAliasBeingEdited) {
                editUserAlias({
                  id: userAliasBeingEdited.alias.id,
                  fromName,
                  address,
                });
              } else {
                addUserAlias({
                  fromName,
                  address,
                });
              }
              closeModal();
            }}
          >
            Add
          </StyledButton>
        </Buttons>
      </Bottom>
    </Body>
  );
}
